/**
 * Mapa a pantalla grande.
 *
 * El mapa de la ficha mide 168 píxeles de alto y no se puede mover: sirve para SITUAR el
 * inmueble de un vistazo, no para explorar el barrio. Para lo segundo está esto: pinchas en el
 * mapa pequeño y se abre el mismo mapa ocupando casi toda la pantalla, ya interactivo (rueda
 * para acercar, arrastrar para desplazar, botones de zoom).
 *
 * Sigue las mismas reglas que la vista ampliada de fotos (`Lightbox`): Esc y clic fuera
 * cierran, el foco entra al abrir y vuelve a donde estaba al cerrar, y el fondo no se
 * desplaza mientras está abierto.
 */
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import { PropertyMap } from './LazyPropertyMap';
import type { MapMarker } from './PropertyMap';
import styles from './MapModal.module.css';

interface MapModalProps {
  title: string;
  subtitle?: string;
  markers: MapMarker[];
  onClose: () => void;
}

export function MapModal({ title, subtitle, markers, onClose }: MapModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);
  const titleId = useId();

  useEffect(() => {
    openerRef.current = document.activeElement;
    dialogRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className={styles.scrim}
      // Pinchar FUERA del mapa cierra; pinchar dentro, no: ahí se está usando el mapa.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div className={styles.heading}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Cerrar">
            <Icon name="close" size={18} strokeWidth={2.2} />
          </button>
        </header>

        <div className={styles.body}>
          <PropertyMap markers={markers} height="100%" />
        </div>

        <p className={styles.hint}>Usa la rueda para acercar, arrastra para moverte y pulsa Esc para cerrar</p>
      </div>
    </div>,
    document.body,
  );
}
